<script setup lang="ts">
import type { ILoginResponse } from '@/entities/auth'
import type { ILoginForm } from '@/shared/lib'
import { Loader2 } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useLoginMutation } from '@/entities/auth'
import { HttpError } from '@/shared/api/errors'
import { setAuthTokens, useCustomToast } from '@/shared/lib'
import CDialog from '@/shared/ui/CDialog.vue'

defineEmits<Emits>()
const { useAuthStore } = await import('@/entities/auth')
const { useStoresStore } = await import('@/entities/store')

const { t } = useI18n()
const router = useRouter()
const { error: showError, success } = useCustomToast()

interface Emits {
  (eventName: 'submit', eventData: ILoginForm): void
  (e: 'showForgetPasswordDialog'): void
  (e: 'showRegisterDialog'): void
}
const showLoginDialog = defineModel<boolean>()
const isLoading = ref(false)

const loginForm = ref<ILoginForm>({
  email: '',
  password: '',
})

const { mutate: login } = useLoginMutation()

// Check if form is valid (both email and password are filled)
const isFormValid = computed(() => {
  const email = loginForm.value.email.trim()
  const password = loginForm.value.password.trim()

  // Basic email validation
  const emailRegex = /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/
  const isEmailValid = emailRegex.test(email)

  return email !== '' && password !== '' && isEmailValid
})

// Email error message
const emailError = computed(() => {
  const email = loginForm.value.email.trim()
  if (email === '') 
    return ''

  const emailRegex = /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/
  return emailRegex.test(email) ? '' : 'Please enter a valid email address'
})

function onSubmit() {
  isLoading.value = true
  login(loginForm.value, {
    onSuccess: async (data: ILoginResponse) => {
      setAuthTokens(data.access, data.refresh)

      // Import stores here to avoid circular dependency
      const authStore = useAuthStore()
      const storesStore = useStoresStore()

      // Fetch profile after successful login
      await authStore.fetchProfile()

      // Fetch stores after successful login
      await storesStore.fetchStores()

      isLoading.value = false
      showLoginDialog.value = false

      // Show success toast
      success({
        title: t('successful_login'),
        message: t('successful_login_message'),
      })

      router.push({ name: 'Dashboard' })
    },
    onError: async (err: HttpError | Error | unknown) => {
      isLoading.value = false
      console.error('Login error:', err)

      let errorMessage = 'An error occurred during login'

      // Check if error is HttpError
      if (err instanceof HttpError) {
        // Check if errorData is already parsed
        if (err.errorData) {
          // Use errorData if already parsed - handle both detail and message fields
          errorMessage
            = (err.errorData as { detail?: string, message?: string }).detail
            || err.errorData.message
            || errorMessage
        }
        else if (err.parseError) {
          // Parse error if not already parsed
          try {
            const errorData = await err.parseError()
            if (errorData) {
              // Handle both detail and message fields from parsed error
              const parsedError = errorData as {
                detail?: string
                message?: string
              } & typeof errorData
              errorMessage
                = parsedError.detail || errorData.message || errorMessage
            }
          }
          catch (parseErr) {
            console.error('Failed to parse error:', parseErr)
          }
        }
        else if (err.response) {
          // Try to parse response directly
          try {
            const clonedResponse = err.response.clone()
            const errorData = (await clonedResponse.json()) as {
              detail?: string
              message?: string
            }
            errorMessage = errorData.detail || errorData.message || errorMessage
          }
          catch (parseErr) {
            console.error('Failed to parse response:', parseErr)
          }
        }
      }
      else if (err instanceof Error) {
        errorMessage = err.message
      }

      showError({
        title: 'Ошибка входа',
        message: errorMessage,
      })
    },
  })
}
</script>

<template>
  <CDialog
    v-model="showLoginDialog"
    :title="t('login')"
    width="480"
    class="!w-[90%] sm:!w-[480px]"
  >
    <ElForm label-position="top" @submit.prevent="onSubmit">
      <!-- Email -->
      <ElFormItem label="E-mail" :error="emailError">
        <ElInput
          v-model="loginForm.email"
          :placeholder="t('enter_email')"
          :class="{ 'is-error': emailError }"
        />
      </ElFormItem>

      <!-- Password -->
      <ElFormItem label="Password">
        <template #label>
          <div class="flex items-center justify-between">
            <span>{{ t("password") }}</span>
            <ElLink type="primary" @click="$emit('showForgetPasswordDialog')">
              {{ t("forgot_password_question") }}
            </ElLink>
          </div>
        </template>
        <ElInput
          v-model="loginForm.password"
          type="password"
          :placeholder="t('enter_password')"
          show-password
        />
      </ElFormItem>
    </ElForm>

    <template #footer>
      <div
        class="flex items-center justify-between max-sm:flex-col-reverse max-sm:gap-4"
      >
        <p class="flex items-center gap-2">
          <ElText>{{ t("no_account") }}</ElText>
          <ElLink type="primary" @click="$emit('showRegisterDialog')">
            {{ t("register") }}
          </ElLink>
        </p>
        <ElButton
          type="primary"
          class="w-full sm:w-[108px]"
          :disabled="!isFormValid"
          @click="onSubmit"
        >
          {{ t("login") }}
          <Loader2 v-if="isLoading" class="animate-spin text-sm w-4 h-4 ml-2" />
        </ElButton>
      </div>
    </template>
  </CDialog>
</template>
